import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
    Edit, Trash2,
    TrendingUp, TrendingDown, PiggyBank,
    ChevronLeft, ChevronRight,
    Clock, Calendar,
} from 'lucide-react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import { toast } from 'react-hot-toast';
import 'react-datepicker/dist/react-datepicker.css';

const TYPE_CONFIG = {
    income: {
        label: 'Income', Icon: TrendingUp,
        color: 'text-emerald-400',
        bg: 'bg-emerald-400/10',
        border: 'border-emerald-400/30',
        badge: 'bg-emerald-400/20 text-emerald-300',
        row: 'row-income',
    },
    expense: {
        label: 'Expense', Icon: TrendingDown,
        color: 'text-rose-400',
        bg: 'bg-rose-400/10',
        border: 'border-rose-400/30',
        badge: 'bg-rose-400/20 text-rose-300',
        row: 'row-expense',
    },
    savings: {
        label: 'Savings', Icon: PiggyBank,
        color: 'text-sky-400',
        bg: 'bg-sky-400/10',
        border: 'border-sky-400/30',
        badge: 'bg-sky-400/20 text-sky-300',
        row: 'row-savings',
    },
};

const CATEGORIES = ['Food', 'Transport', 'Utilities', 'Entertainment', 'Health', 'Others'];
const API = 'https://expense-tracker-server-puce.vercel.app';

const inputCls =
    'w-full bg-white/[0.03] border border-slate-800 rounded-xl px-4 py-2.5 text-slate-100 placeholder-slate-700 focus:outline-none focus:border-emerald-500/50 transition-colors text-sm font-mono';

export default function InputEntry() {
    const [user, setUser] = useState(null);
    const [formType, setFormType] = useState('');
    const [formData, setFormData] = useState({ amount: '', category: '', note: '', date: new Date() });
    const [entries, setEntries] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [currentTime, setCurrentTime] = useState(new Date());
    const [totalIncome, setTotalIncome] = useState(0);
    const [editingId, setEditingId] = useState(null);

    useEffect(() => { setUser(JSON.parse(localStorage.getItem('user'))); }, []);
    useEffect(() => { const t = setInterval(() => setCurrentTime(new Date()), 1000); return () => clearInterval(t); }, []);
    useEffect(() => {
        if (!user?._id) return;
        axios.get(`${API}/entries/${user._id}`)
            .then(r => { setEntries(r.data); calcIncome(r.data); })
            .catch(console.error);
    }, [user?._id]);

    const calcIncome = (data) =>
        setTotalIncome(data.filter(e => e.type === 'income').reduce((s, e) => s + e.amount, 0));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const userId = JSON.parse(localStorage.getItem('user'))?._id;
        if (!formData.amount || !formType || !userId) { toast.error('Please fill out all required fields'); return; }
        try {
            const res = await axios.post(`${API}/add-entry`, { ...formData, type: formType, userId });
            const next = [...entries, res.data];
            setEntries(next); calcIncome(next);
            setFormData({ amount: '', category: '', note: '', date: new Date() });
            setFormType(''); setEditingId(null);
            toast.success('Entry added!');
        } catch { toast.error('Error adding entry. Please try again.'); }
    };

    const handleEdit = (id) => {
        const entry = entries.find(e => e._id === id);
        if (!entry) return;
        setFormData({ amount: entry.amount, category: entry.category, note: entry.note, date: new Date(entry.date) });
        setFormType(entry.type); setEditingId(id);
        setEntries(entries.filter(e => e._id !== id));
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API}/entries/${id}`);
            const next = entries.filter(e => e._id !== id);
            setEntries(next); calcIncome(next);
        } catch { console.error('Delete failed'); }
    };

    const shiftMonth = (dir) => {
        const d = new Date(currentMonth); d.setMonth(d.getMonth() + dir); setCurrentMonth(d);
    };

    const filteredEntries = entries.filter(e =>
        new Date(e.date).getMonth() === currentMonth.getMonth() &&
        new Date(e.date).getFullYear() === currentMonth.getFullYear()
    );

    const monthStr = currentMonth.toISOString().slice(0, 7);
    const monthExp = filteredEntries.filter(e => e.type === 'expense').reduce((s, e) => s + e.amount, 0);
    const monthInc = filteredEntries.filter(e => e.type === 'income').reduce((s, e) => s + e.amount, 0);
    const monthSav = filteredEntries.filter(e => e.type === 'savings').reduce((s, e) => s + e.amount, 0);
    const totalExpAll = entries.filter(e => e.type === 'expense' && new Date(e.date).toISOString().slice(0, 7) === monthStr).reduce((s, e) => s + e.amount, 0);
    const balance = totalIncome - totalExpAll;

    const cfg = TYPE_CONFIG[formType] || {};

    const SUMMARY = [
        { label: 'Balance', value: balance, color: 'text-emerald-400', sub: 'All time' },
        { label: 'Income', value: monthInc, color: 'text-emerald-400', sub: 'This month' },
        { label: 'Expenses', value: monthExp, color: 'text-rose-400', sub: 'This month' },
        { label: 'Savings', value: monthSav, color: 'text-sky-400', sub: 'This month' },
    ];

    return (
        <div className="min-h-screen bg-[#0a0d12] text-slate-100 font-sans">
            <Helmet><title>Input — Expense Tracker</title></Helmet>

            <div className="max-w-[1400px] mx-auto px-4 py-8 space-y-6">

                {/* ── Header ── */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-syne text-2xl font-bold tracking-tight">
                            Financial <span className="text-emerald-400">Ledger</span>
                        </h1>
                        <p className="text-slate-500 text-sm mt-0.5 font-mono">Track every cent, grow every dollar</p>
                    </div>

                    {/* Live clock */}
                    <div className="glass rounded-2xl px-5 py-3 flex items-center gap-4">
                        <div className="flex items-center gap-2 text-slate-400 text-sm">
                            <Calendar size={14} className="text-emerald-400" />
                            <span className="font-mono">
                                {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                        </div>
                        <div className="w-px h-4 bg-slate-700" />
                        <div className="flex items-center gap-2 text-slate-400 text-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
                            <Clock size={14} className="text-emerald-400" />
                            <span className="font-mono">
                                {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ── Summary Cards ── */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {SUMMARY.map(({ label, value, color, sub }) => (
                        <div key={label} className="glass rounded-2xl p-4 space-y-1">
                            <p className="text-xs text-slate-500 uppercase tracking-widest">{label}</p>
                            <p className={`text-xl font-semibold font-mono ${color}`}>${value.toLocaleString()}</p>
                            <p className="text-xs text-slate-600">{sub}</p>
                        </div>
                    ))}
                </div>

                {/* ── Main Layout ── */}
                <div className="flex flex-col lg:flex-row gap-5">

                    {/* ── Form ── */}
                    <form onSubmit={handleSubmit} className="glass rounded-2xl p-6 lg:w-[340px] shrink-0 space-y-5">
                        <h2 className="text-sm font-medium text-slate-400 uppercase tracking-widest">
                            {editingId ? '✎ Edit Entry' : '+ New Entry'}
                        </h2>

                        {/* Type toggle buttons */}
                        <div className="grid grid-cols-3 gap-2">
                            {Object.entries(TYPE_CONFIG).map(([type, c]) => {
                                const active = formType === type;
                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => setFormType(type)}
                                        className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-all duration-200 text-xs font-medium
                      ${active
                                                ? `${c.bg} ${c.border} ${c.color}`
                                                : 'border-slate-800 text-slate-500 hover:border-slate-600 hover:text-slate-300'
                                            }`}
                                    >
                                        <c.Icon size={16} />
                                        {c.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Amount */}
                        <div className="space-y-1.5">
                            <label className="text-xs text-slate-500 uppercase tracking-widest">Amount</label>
                            <div className="relative">
                                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 font-mono text-sm">$</span>
                                <input
                                    type="number"
                                    value={formData.amount}
                                    onChange={e => setFormData({ ...formData, amount: e.target.value })}
                                    placeholder="0.00"
                                    required
                                    className={`${inputCls} pl-8`}
                                />
                            </div>
                        </div>

                        {/* Category / Source */}
                        <div className="space-y-1.5">
                            <label className="text-xs text-slate-500 uppercase tracking-widest">
                                {formType === 'income' ? 'Source' : formType === 'savings' ? 'Savings Type' : 'Category'}
                            </label>
                            {formType === 'expense' ? (
                                <select
                                    value={formData.category}
                                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                                    className={`${inputCls} appearance-none`}
                                >
                                    <option value="" disabled>Select category</option>
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    value={formData.category}
                                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                                    placeholder={
                                        formType === 'income' ? 'e.g. Salary, Freelance' :
                                            formType === 'savings' ? 'e.g. Emergency fund' : '—'
                                    }
                                    className={inputCls}
                                />
                            )}
                        </div>

                        {/* Note */}
                        <div className="space-y-1.5">
                            <label className="text-xs text-slate-500 uppercase tracking-widest">
                                Note <span className="normal-case text-slate-700">(optional)</span>
                            </label>
                            <input
                                type="text"
                                value={formData.note}
                                onChange={e => setFormData({ ...formData, note: e.target.value })}
                                placeholder="Add a note…"
                                className={inputCls}
                            />
                        </div>

                        {/* Date */}
                        <div className="space-y-1.5 datepicker-dark">
                            <label className="text-xs text-slate-500 uppercase tracking-widest">Date</label>
                            <DatePicker
                                selected={formData.date}
                                onChange={date => setFormData({ ...formData, date })}
                                dateFormat="dd/MM/yyyy"
                                required
                                className={inputCls}
                            />
                        </div>

                        <button
                            type="submit"
                            className={`w-full py-3 rounded-xl font-medium text-sm transition-all duration-200
                ${formType
                                    ? `${cfg.bg} ${cfg.color} border ${cfg.border} hover:opacity-80`
                                    : 'bg-emerald-500 text-slate-900 hover:bg-emerald-400'
                                }`}
                        >
                            {editingId ? 'Update Entry' : 'Add Entry'}
                        </button>
                    </form>

                    {/* ── Table ── */}
                    <div className="flex-1 glass rounded-2xl p-6 space-y-4 min-w-0">

                        {/* Month nav */}
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => shiftMonth(-1)}
                                className="p-2 rounded-xl border border-slate-800 hover:border-slate-600 text-slate-400 hover:text-slate-200 transition-colors"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <h2 className="font-syne text-sm font-semibold text-slate-300">
                                {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                            </h2>
                            <button
                                onClick={() => shiftMonth(1)}
                                className="p-2 rounded-xl border border-slate-800 hover:border-slate-600 text-slate-400 hover:text-slate-200 transition-colors"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>

                        {/* Entries */}
                        <div className="overflow-x-auto">
                            {filteredEntries.length === 0 ? (
                                <div className="py-16 text-center text-slate-600 text-sm">
                                    <PiggyBank size={32} className="mx-auto mb-3 opacity-30" />
                                    No entries for this month
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs text-slate-600 uppercase tracking-widest">
                                            <th className="pb-3 pr-4">Type</th>
                                            <th className="pb-3 pr-4">Amount</th>
                                            <th className="pb-3 pr-4">Category</th>
                                            <th className="pb-3 pr-4">Date</th>
                                            <th className="pb-3 pr-4">Note</th>
                                            <th className="pb-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800/60">
                                        {filteredEntries.map(entry => {
                                            const c = TYPE_CONFIG[entry.type] || {};
                                            return (
                                                <tr
                                                    key={entry._id}
                                                    className={`${c.row} animate-slide-in hover:bg-white/[0.02] transition-colors`}
                                                >
                                                    <td className="py-3 pr-4">
                                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${c.badge}`}>
                                                            {entry.type}
                                                        </span>
                                                    </td>
                                                    <td className={`py-3 pr-4 font-mono font-medium ${c.color}`}>
                                                        ${entry.amount.toLocaleString()}
                                                    </td>
                                                    <td className="py-3 pr-4 text-slate-400">{entry.category || '—'}</td>
                                                    <td className="py-3 pr-4 text-slate-500 font-mono text-xs">
                                                        {new Date(entry.date).toLocaleDateString('en-GB')}
                                                    </td>
                                                    <td className="py-3 pr-4 text-slate-500 max-w-[140px] truncate">{entry.note || '—'}</td>
                                                    <td className="py-3 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <button
                                                                onClick={() => handleEdit(entry._id)}
                                                                className="p-1.5 rounded-lg border border-slate-800 hover:border-sky-500/50 hover:text-sky-400 text-slate-500 transition-colors"
                                                            >
                                                                <Edit size={13} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(entry._id)}
                                                                className="p-1.5 rounded-lg border border-slate-800 hover:border-rose-500/50 hover:text-rose-400 text-slate-500 transition-colors"
                                                            >
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}